export type Example = {
  id: string;
  purpose: string;
  example: string;
  whyItWorks: string;
  doNotCopy: string;
};

export const examples: Record<string, Example> = {
  decision: {
    id: "decision",
    purpose: "Name the decision, not the vague hope around it.",
    example:
      "After reading, two producers should decide whether to finance the next writing pass and introduce the project to one packaging partner.",
    whyItWorks: "It names who decides, what they decide, and what action follows.",
    doNotCopy: "Do not replace your real decision with ‘create awareness’ or ‘get people excited’. Those are feelings, not decisions.",
  },
  filmLogline: {
    id: "filmLogline",
    purpose: "Hold protagonist, pressure, and the dramatic proposition in one breath.",
    example:
      "When a debt-ridden wedding singer discovers she can hear the final thought of anyone who lies to her, she has one night to expose the groom financing her escape.",
    whyItWorks: "A person, unusual pressure, urgent action, and a collision are all visible.",
    doNotCopy: "Do not imitate the supernatural hook. Keep the shape; use your story.",
  },
  synopsis: {
    id: "synopsis",
    purpose: "Show the story’s movement, not every event.",
    example:
      "Mira takes the last singing job before leaving town. At the wedding, every lie arrives in someone else’s voice. What begins as a private advantage turns dangerous when she hears the groom’s plan to frame the bride. Mira must choose between the money that buys her freedom and the truth that may cost it. By dawn, the celebration has become a public trial—and Mira is no longer invisible inside other people’s stories.",
    whyItWorks: "It establishes setup, escalation, choice, and change without becoming a scene list.",
    doNotCopy: "Do not hide the ending merely to sound mysterious. A deck may need more story truth than a trailer.",
  },
  seriesEngine: {
    id: "seriesEngine",
    purpose: "Explain what produces a new episode after the pilot is over.",
    example:
      "Each episode begins with a different resident asking the building’s unofficial night concierge to solve a small problem. The favour exposes a larger secret connecting the residents, while the concierge’s own missing history advances across the season.",
    whyItWorks: "It shows a repeatable weekly mechanism and a continuing story.",
    doNotCopy: "Do not mistake ‘the characters face new challenges’ for an engine. Name the repeatable pressure.",
  },
  adIdea: {
    id: "adIdea",
    purpose: "Tell us the idea, emotional movement, and product role.",
    example:
      "A city wakes before its people do. Streetlights, kettles, lifts, and bakery shutters quietly pass a single pulse between them. The first commuter taps the product and the pulse becomes music. The product does not interrupt the morning; it reveals the energy already moving through it.",
    whyItWorks: "The film has a visual rule, an emotional turn, and a necessary product role.",
    doNotCopy: "Do not write camera coverage yet. First make the idea intelligible.",
  },
  businessProblem: {
    id: "businessProblem",
    purpose: "Describe a costly situation a specific person already recognises.",
    example:
      "Independent clinics lose referrals because patient records arrive by email, WhatsApp, and paper. Staff spend hours rebuilding one history, while doctors still begin consultations with missing information.",
    whyItWorks: "The customer, broken workflow, cost, and consequence are concrete.",
    doNotCopy: "Avoid ‘the industry is inefficient’. Show where the inefficiency lives.",
  },
  creatorNote: {
    id: "creatorNote",
    purpose: "Connect lived authority to this project and this moment.",
    example:
      "I grew up above my mother’s tailoring shop, where people told the truth only after the fitting-room curtain closed. This story began with those half-heard confessions. I am making it now because the neighbourhood is being demolished, and the private archive carried by its workers is disappearing with it.",
    whyItWorks: "It joins memory, authority, subject, and urgency without claiming universal importance.",
    doNotCopy: "Do not manufacture trauma or destiny. Plain, specific truth carries more weight.",
  },
  references: {
    id: "references",
    purpose: "Separate the useful mechanism from the surface you do not want copied.",
    example:
      "Reference: The hard flash and crowded crops in the campaign make every frame feel stolen from a real night. Keep that proximity. Do not borrow its neon palette or type treatment.",
    whyItWorks: "It says what transfers and what must stay behind.",
    doNotCopy: "A link alone is not direction. Explain the part you mean.",
  },
  evidence: {
    id: "evidence",
    purpose: "Tie an important claim to something another person can inspect.",
    example:
      "Claim: 63% of invited clinics completed onboarding. Source: May 2026 onboarding export, rows 14–81. Status: internally verified; not externally audited.",
    whyItWorks: "The claim, source, location, and confidence boundary stay together.",
    doNotCopy: "Do not turn an estimate into a fact because the slide needs a number.",
  },
};
